// covered-by: frontends/carrie/src/ask-v2/project/__tests__/AskV2OpenItems.test.tsx
import { type AskPunttOpenItem } from "@mg/schemas/src/christo/askPunttOpenItems";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { type OpenItemThreadQuestion } from "./openItemThreadQuestions";

import {
  formatQuestionFormAnswerLine,
  questionOptionAnswerText,
} from "../../utils/questionFormAnswerLine";

export function AskV2OpenItemAnswer({
  item,
  question,
  disabled,
  onAttach,
  onSend,
}: {
  item: AskPunttOpenItem;
  question: OpenItemThreadQuestion | null;
  disabled: boolean;
  onAttach: (item: AskPunttOpenItem) => void;
  onSend: (message: string) => void;
}) {
  const { t } = useTranslation("ui");
  const [text, setText] = useState("");
  const inputId = `ask-v2-open-item-answer-${item.id}`;
  const typedAnswerLabel =
    question != null
      ? t("askV2.openItems.answerForQuestion", {
          question: question.header,
        })
      : t("orTypeYourOwn");
  // why: attach then send — the thread's send reads the open-item chip that attach just wrote
  const answer = (message: string) => {
    onAttach(item);
    onSend(message);
  };
  const submitTypedAnswer = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return;
    answer(
      question != null
        ? formatQuestionFormAnswerLine(question.header, trimmed)
        : trimmed,
    );
  };
  return (
    <div className="flex flex-col gap-2">
      {question != null ? (
        <div
          role="group"
          aria-label={question.header}
          data-testid={`ask-v2-open-item-choices-${item.id}`}
          className="flex flex-col gap-2"
        >
          <p
            className="text-[13px] font-medium leading-snug text-puntt-neutral-gray-12"
            data-testid={`ask-v2-open-item-choice-header-${item.id}`}
          >
            {question.header}
          </p>
          {question.options.map((option, index) => {
            const choiceNumber = index + 1;
            // why: ask-v2 cannot import the V1 choice row; this is the same option label and detail the thread form already drew
            const detail = option.description ?? "";
            return (
              <button
                key={`${item.id}-${choiceNumber}`}
                type="button"
                data-testid={`ask-v2-open-item-choice-${item.id}-${choiceNumber}`}
                disabled={disabled}
                title={detail.trim().length > 0 ? detail : undefined}
                className="flex w-full min-w-0 items-center justify-between gap-2 overflow-hidden rounded-lg border border-transparent bg-transparent px-3 py-2 text-left outline-none hover:bg-puntt-neutral-gray-3 disabled:cursor-default disabled:opacity-40"
                onClick={() => {
                  answer(
                    formatQuestionFormAnswerLine(
                      question.header,
                      questionOptionAnswerText(option),
                    ),
                  );
                }}
              >
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-puntt-neutral-gray-11">
                  {option.label}
                </span>
                {detail.trim().length > 0 ? (
                  <span className="max-w-[45%] shrink-0 truncate text-xs text-puntt-neutral-gray-11">
                    {detail}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
      <form
        data-testid={`ask-v2-open-item-answer-form-${item.id}`}
        aria-label={typedAnswerLabel}
        className="flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (disabled) return;
          submitTypedAnswer(text);
          setText("");
        }}
      >
        <input
          id={inputId}
          data-testid={inputId}
          className="min-w-0 flex-1 rounded-md border border-puntt-neutral-gray-6 bg-base-white px-2 py-1.5 text-[13px] text-puntt-neutral-gray-12"
          placeholder={t("orTypeYourOwn")}
          aria-label={typedAnswerLabel}
          value={text}
          disabled={disabled}
          onChange={(event) => setText(event.target.value)}
        />
        <button
          type="submit"
          data-testid={`ask-v2-open-item-answer-submit-${item.id}`}
          aria-label={t("askV2.openItems.submitAnswerForItem", {
            item: item.needed,
          })}
          className="shrink-0 rounded-md px-2 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12"
          disabled={disabled}
        >
          {t("submitAnswers")}
        </button>
      </form>
    </div>
  );
}
